import React from "react";
import { CopyButton, Table, Text, Tooltip } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import type { ReferralDashboardEntry } from "@/utils/api/referrals";
import {
  getPaymentStatusBadge,
  getSignupSourceBadge,
} from "../utils/badgeRenderers";
import { RefereeNameCell } from "./RefereeNameCell";
import { ExpandableRewardDetails } from "./ExpandableRewardDetails";

interface ReferralTableRowProps {
  item: ReferralDashboardEntry;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

/**
 * Component for displaying a single referral table row with expandable details
 */
export const ReferralTableRow: React.FC<ReferralTableRowProps> = ({
  item,
  isExpanded,
  onToggleExpand,
}) => {
  const hasEmail =
    item.refereeEmail &&
    item.refereeEmail.trim() !== "" &&
    item.refereeEmail !== "Pending";

  const hasRewards =
    item.referrerRewards.length > 0 || item.refereeRewards.length > 0;

  return (
    <React.Fragment key={item.id}>
      <Table.Tr
        style={{
          borderBottom: isExpanded ? "none" : "1px solid #e9ecef",
          cursor: hasRewards ? "pointer" : "default",
        }}
        onClick={() => hasRewards && onToggleExpand()}
      >
        {/* Expand Icon */}
        <Table.Td style={{ padding: "12px", width: "40px" }}>
          {hasRewards ? (
            isExpanded ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )
          ) : null}
        </Table.Td>

        {/* Referee Name */}
        <Table.Td style={{ padding: "12px" }}>
          <RefereeNameCell
            refereeName={item.refereeName}
            refereeEmail={item.refereeEmail}
          />
        </Table.Td>

        {/* Email */}
        <Table.Td style={{ padding: "12px" }}>
          {hasEmail ? (
            <CopyButton value={item.refereeEmail || ""}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied!" : "Click to copy"}>
                  <Text
                    size="sm"
                    c="dark"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      copy();
                    }}
                  >
                    {item.refereeEmail}
                  </Text>
                </Tooltip>
              )}
            </CopyButton>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>

        {/* Signup Source */}
        <Table.Td style={{ padding: "12px" }}>
          {getSignupSourceBadge(item.signupSource)}
        </Table.Td>

        {/* Payment Status */}
        <Table.Td style={{ padding: "12px" }}>
          {getPaymentStatusBadge(item.paymentStatus)}
        </Table.Td>

        {/* Reward Amount */}
        <Table.Td style={{ padding: "12px" }}>
          <Text size="sm" fw={600} c="teal.7">
            ${item.rewardAmount.toFixed(2)}
          </Text>
        </Table.Td>

        {/* Date Created */}
        <Table.Td style={{ padding: "12px" }}>
          {item.createdAt ? (
            <Text size="sm">
              {new Date(item.createdAt).toLocaleDateString()}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              -
            </Text>
          )}
        </Table.Td>
      </Table.Tr>

      {/* Expandable Reward Details */}
      {hasRewards && (
        <Table.Tr
          style={{
            borderBottom: "1px solid #e9ecef",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Table.Td colSpan={7} style={{ padding: 0 }}>
            <ExpandableRewardDetails
              isExpanded={isExpanded}
              referrerRewards={item.referrerRewards}
              refereeRewards={item.refereeRewards}
              totalRewardAmount={item.rewardAmount}
            />
          </Table.Td>
        </Table.Tr>
      )}
    </React.Fragment>
  );
};
